// src/lib/cache/entityCache.js
//
// Per-entity React Query primitives for the mega-query migration. Each entity
// batch wires its existing api/* functions to these primitives, replaces its
// accessor in ReactQueryDataProvider, and removes itself from the mega-query's
// essential phase IN THE SAME COMMIT.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSupabaseAuth } from '../useSupabaseAuth.jsx'
import { CACHE_TIMES } from '../query-client'

// Canonical key shape: [entityName, companyId]. Detail variants extend.
export const entityKeys = {
  list: (entityName, companyId) => [entityName, companyId],
  detail: (entityName, companyId, id) => [entityName, companyId, 'detail', id],
}

// Wrap a `{ data, error }` API fetcher into a (companyId) => row[] used by
// useEntityListQuery. Centralises the "throw on error, default to []" shape
// every entity file used to inline.
export const unwrapList = (label, fetcher) => async (companyId) => {
  const result = await fetcher(companyId)
  if (result?.error && !result?.data) {
    throw new Error(result.error.message || `Failed to load ${label}`)
  }
  return result?.data ?? []
}

// Whole-row replace by id from the server response. Used as the standard
// update mutation reconcile across every entity.
export const defaultUpdateReconcile = (prev, serverRow) =>
  prev.map((row) => (row.id === serverRow.id ? serverRow : row))

// Replace the tempId placeholder with the server row. Defensive: if the
// placeholder was already evicted (raced by a sibling cancelQueries), make
// sure the real row still lands in cache exactly once. Used as the standard
// create mutation reconcile.
export const defaultCreateReconcile = (prev, serverRow, ctx) => {
  const tempId = ctx?.tempId
  const replaced = prev.map((row) => (row.id === tempId ? serverRow : row))
  return replaced.some((row) => row.id === serverRow.id)
    ? replaced
    : [serverRow, ...replaced]
}

const defaultListOptions = {
  staleTime: CACHE_TIMES.ESSENTIAL_STALE,
  gcTime: CACHE_TIMES.GC_TIME,
  retry: 3,
  retryDelay: (i) => Math.min(1000 * 2 ** i, 30000),
}

export const useEntityListQuery = (entityName, fetcher, options = {}) => {
  const { companyId, authReady } = useSupabaseAuth()
  return useQuery({
    queryKey: entityKeys.list(entityName, companyId),
    queryFn: () => fetcher(companyId),
    enabled: !!companyId && authReady,
    ...defaultListOptions,
    ...options,
  })
}

// Returns the { data, loading, error, isLoaded } shape that existing context
// consumers already expect, derived from a useQuery result.
export const toContextAccessor = (queryResult) => ({
  data: queryResult.data ?? [],
  loading: queryResult.isLoading,
  error: queryResult.error ?? null,
  isLoaded: queryResult.isSuccess,
})

const makeTempId = () => `tmp-${crypto.randomUUID()}`

// Sentinel values an optimistic placeholder is allowed to carry at a deferred
// field. Anything else means the caller predicted the server-fabricated value,
// which the deferred-field contract forbids.
const DEFERRED_FIELD_SENTINELS = new Set([undefined, null, '', '…'])

// Optimistic list mutation.
//
//   applyOptimistic(prev, vars, ctx) -> nextList
//     For inserts, use ctx.tempId as the placeholder row id so reconcile can
//     find and replace it. For updates/deletes, ctx.tempId is irrelevant.
//
//   reconcile(prev, serverRow, ctx) -> nextList
//     Called in onSuccess with the row the server returned. Replace the
//     placeholder by ctx.tempId for inserts; merge serverRow over the matching
//     id for updates; no-op for deletes.
//
//   deferredField (optional)
//     Name of a field the server fabricates and the client must not predict
//     (e.g. estimates' estimate_number, generated by the next_estimate_number
//     RPC). The DB layer is already safe — row-lock on companies plus
//     UNIQUE(estimate_number, company_id) means the server never issues
//     duplicates. The reason to defer rather than predict is purely UX:
//     a predicted number can be wrong-but-still-succeed (the INSERT lands
//     with a different real number) and the optimistic row would visibly
//     flip on reconcile. Showing a sentinel instead keeps the displayed
//     value honest. Only meaningful for the create path.
//
// Per the migration spec: invalidate ONLY on the error path. The success path
// reconciles in-place so we avoid an unnecessary refetch round trip.
export const useOptimisticListMutation = (
  entityName,
  { mutationFn, applyOptimistic, reconcile, deferredField }
) => {
  const queryClient = useQueryClient()
  const { companyId } = useSupabaseAuth()
  const key = entityKeys.list(entityName, companyId)

  return useMutation({
    mutationFn,
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData(key)
      const ctx = { tempId: makeTempId(), vars }
      queryClient.setQueryData(key, (prev) => applyOptimistic(prev ?? [], vars, ctx))

      if (process.env.NODE_ENV !== 'production' && deferredField) {
        const after = queryClient.getQueryData(key)
        const placeholder = Array.isArray(after)
          ? after.find((row) => row?.id === ctx.tempId)
          : null
        if (placeholder && !DEFERRED_FIELD_SENTINELS.has(placeholder[deferredField])) {
          throw new Error(
            `[entityCache:${entityName}] optimistic placeholder set field ` +
            `'${deferredField}' to ${JSON.stringify(placeholder[deferredField])}; ` +
            `that field is server-fabricated and must be left as a sentinel ` +
            `(undefined, null, '', or '…') so the displayed value does not ` +
            `flip on reconcile.`
          )
        }
      }

      return { previous, ctx }
    },
    onSuccess: (serverRow, _vars, mutationCtx) => {
      queryClient.setQueryData(key, (prev) =>
        reconcile(prev ?? [], serverRow, mutationCtx?.ctx)
      )
    },
    onError: (_err, _vars, mutationCtx) => {
      if (mutationCtx?.previous !== undefined) {
        queryClient.setQueryData(key, mutationCtx.previous)
      }
      queryClient.invalidateQueries({ queryKey: key })
    },
  })
}

// Refetch-after list mutation. Use for entities where the server rejects or
// transforms inserts in ways the client cannot model (e.g. employees'
// slot-limit trigger). No optimistic write; invalidate on success so the
// next read picks up the real row. For server-fabricated fields like
// estimate_number, prefer useOptimisticListMutation + deferredField.
export const useRefetchListMutation = (entityName, { mutationFn }) => {
  const queryClient = useQueryClient()
  const { companyId } = useSupabaseAuth()
  const key = entityKeys.list(entityName, companyId)

  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: key })
    },
  })
}

// Standard delete mutation: optimistically drop the row by id, no-op on
// success (the row is already gone from cache). The deleteFn receives the
// id as passed by the caller.
export const useStandardDeleteMutation = (entityName, deleteFn) =>
  useOptimisticListMutation(entityName, {
    mutationFn: (id) => deleteFn(id),
    applyOptimistic: (prev, id) => prev.filter((row) => row.id !== id),
    reconcile: (prev) => prev,
  })
